import { FC, useMemo } from "react";
import capitalize from "lodash/capitalize";
import keyBy from "lodash/keyBy";
import orderBy from "lodash/orderBy";
import AccordionList from "@common/components/AccordionList";
import { currencyFormatter } from "@common/utils";
import {
  COLOR_CLASSES_BY_GOAL_TYPE,
  IMPORTANCE_GROUP,
  IMPORTANCE_GROUP_BY_IMPORTANCE,
} from "@modules/financialplan/constants";
import {
  useFinancialPlanExpenseTimeline,
  useFinancialPlanGoals,
} from "@modules/financialplan/queries";
import useStore from "@modules/financialplan/state";
import { ScenarioID } from "@modules/mgp-sso/types";
import LoadingIndicator from "@common/components/LoadingIndicator";
import { Typography } from "@earned/core";

interface FinancialPlanGoalsProps {
  goalType: "needs" | "wants" | "wishes";
}
const FinancialPlanGoals: FC<FinancialPlanGoalsProps> = ({ goalType }) => {
  const { selectedScenario } = useStore();
  const { data: financialGoals, isLoading: isLoadingFinancialGoals } =
    useFinancialPlanGoals({ scenario: selectedScenario });
  const { data: expenseTimeline, isLoading: isLoadingExpenseTimeline } =
    useFinancialPlanExpenseTimeline({ scenario: selectedScenario });

  // Load in background the other scenario.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: _otherExpenseTimeline } = useFinancialPlanExpenseTimeline({
    scenario:
      selectedScenario === ScenarioID.Recommended
        ? ScenarioID.Current
        : ScenarioID.Recommended,
  });

  const goalsById: Record<string, Forme.FinancialGoal> = useMemo(() => {
    return keyBy(financialGoals, "id");
  }, [financialGoals]);

  const goals: Forme.EnrichedGoal[] = useMemo(
    () =>
      orderBy(
        (expenseTimeline?.expenses ?? [])
          .filter(({ importance }) => {
            const group =
              IMPORTANCE_GROUP_BY_IMPORTANCE[importance] ??
              IMPORTANCE_GROUP.Wish;
            switch (goalType) {
              case "needs":
                return group === IMPORTANCE_GROUP.Need;
              case "wants":
                return group === IMPORTANCE_GROUP.Want;
              case "wishes":
                return group === IMPORTANCE_GROUP.Wish;
              default:
                throw "Unsupported Goal Type";
            }
          })
          .reduce((memo, expense) => {
            memo.push({
              ...expense,
              ...goalsById[expense.goalId],
              formattedTotalExpenseAmount: currencyFormatter(
                goalsById[expense.goalId]?.totalExpenseAmount
              ),
            } as unknown as Forme.EnrichedGoal);
            return memo;
          }, [] as Forme.EnrichedGoal[]),
        "totalExpenseAmount",
        ["desc"]
      ),
    [expenseTimeline, goalType, goalsById]
  );

  // TODO: rename props for AccordionList - confusing names
  return (
    <>
      <LoadingIndicator
        isVisible={isLoadingFinancialGoals || isLoadingExpenseTimeline}
      />
      {goals && financialGoals && (
        <div className="px-10 pb-10">
          <div className="pb-8">
            <Typography variant="title-xs" color="primary" weight="medium">
              {capitalize(goalType)}
            </Typography>
          </div>
          <AccordionList
            data={goals}
            getData={(item) => item.summary?.expensePeriods}
            collapsedDataKey="formattedTotalExpenseAmount"
            collapsedLabelKey="name"
            colorClassNames={COLOR_CLASSES_BY_GOAL_TYPE[goalType]}
            expandedLabel={(item) => (
              <div className="flex flex-col">
                <div className="flex min-h-4 flex-row items-center">
                  {item.periodSummary}
                </div>
                {item.recurrenceSummary && (
                  <div className="flex min-h-4 flex-row items-center pt-1">
                    {item.recurrenceSummary}
                  </div>
                )}
              </div>
            )}
            expandedData={(item) => currencyFormatter(item.amount)}
          />
        </div>
      )}
    </>
  );
};

export default FinancialPlanGoals;
